import { useScheduleEnabled } from '@/hooks/useScheduleEnabled';
import { HomePage } from '@/pages/Home';
import { useAssigneeWithinPractice } from '@/pages/Home/useAssigneeWithinPractice';
import { useDefaultOrganization } from '@/pages/Home/useDefaultOrganization';
import { PATIENT_LIST_ORG_STORAGE_KEY } from '@/utils/organizationFilterStorage';
import {
  parseStoredWorklistDue,
  WORKLIST_ASSIGNED_TO_STORAGE_KEY,
  WORKLIST_DUE_STORAGE_KEY,
  type WorklistDueFilter,
} from '@/utils/worklistFilterStorage';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { lazy, Suspense } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';

// Lazy so the morning-briefing module graph (overlay/slideshow/player/appointments) isn't pulled into
// every home-page load — only fetched when the flag renders the button.
const MorningBriefingButton = lazy(async () => ({
  default: (await import('@/pages/CarePlanView/narration/MorningBriefingButton')).MorningBriefingButton,
}));

const WorklistSearchSchema = z.object({
  organizationId: z.string().min(1).optional(),
  /** Assignee filter: a practitioner id, or 'unassigned' for ownerless tasks (issue #8762). */
  assignedTo: z.string().min(1).optional(),
  /** Due-date filter: due today or earlier, no due date, or all tasks. The pre-rename 'overdue' is
   *  still accepted so old bookmarks and shared links keep working; it reads as 'due'. */
  due: z.enum(['due', 'none', 'all', 'overdue']).optional(),
  pageIndex: z.number().int().min(0).default(0),
  /** The task open in the sidebar, and the patient it belongs to. Both live in the URL so the open
   *  task survives a reload and can be shared; a task-line click already knows both. A `task` on its
   *  own is a task that belongs to the practice rather than to a chart. */
  task: z.string().min(1).optional(),
  taskPatientId: z.string().min(1).optional(),
  /** Create mode: the panel opens on a blank form, whose own patient field decides which kind of task
   *  it becomes. A flag rather than an absence, so the state is linkable and survives a reload. */
  composeTask: z.boolean().default(false),
  /** Group the list by patient (issue #10503). A view mode, so it lives in the URL like the filters. */
  groupByPatient: z.boolean().default(false),
  /** Narrow the grouped list to flagged patients, including those with no open task. */
  showFlagged: z.boolean().default(false),
});

const WorklistRoute = (): React.ReactNode => {
  // The day rail is part of the scheduling feature, so it lives behind the same flag as Appointments.
  // No `isReady` gate: nothing redirects on this, so the rail simply appears if and when the flag
  // resolves true — the readiness wait exists to stop a route guard bouncing someone mid-load.
  const { enabled: scheduleEnabled } = useScheduleEnabled();
  const { timelineNarration } = useFlags();
  const {
    organizationId: urlOrganizationId,
    assignedTo: urlAssignedTo,
    due: urlDue,
    pageIndex,
    task,
    taskPatientId,
    composeTask,
    groupByPatient,
    showFlagged,
    // oxlint-disable-next-line no-use-before-define
  } = Route.useSearch();
  // oxlint-disable-next-line no-use-before-define
  const navigate = useNavigate({ from: Route.fullPath });
  const [storedOrganizationId, setStoredOrganizationId] = useLocalStorage<string | undefined>(
    PATIENT_LIST_ORG_STORAGE_KEY,
    undefined,
  );
  const [storedAssignedTo, setStoredAssignedTo] = useLocalStorage<string | undefined>(
    WORKLIST_ASSIGNED_TO_STORAGE_KEY,
    undefined,
  );
  const [storedDue, setStoredDue] = useLocalStorage<WorklistDueFilter | undefined>(WORKLIST_DUE_STORAGE_KEY, undefined);

  // URL param wins (bookmarks/shared links); fall back to the sticky stored value.
  const organizationId = urlOrganizationId ?? storedOrganizationId;
  // The worklist offers no "All organizations", so a caller arriving with nothing picked — first
  // visit, or "all" chosen on the patient list, which shares this stored value — is settled onto one.
  // Replaced rather than pushed: nobody navigated here. The assignee is left for
  // `useAssigneeWithinPractice` to judge against the practice this lands on, rather than cleared the
  // way a pick of their own clears it — most callers' stored assignee is themselves, and their own
  // practice lists them.
  useDefaultOrganization(organizationId, (defaultOrganizationId) => {
    setStoredOrganizationId(defaultOrganizationId);
    void navigate({
      search: (prev) => ({ ...prev, organizationId: defaultOrganizationId, pageIndex: 0 }),
      replace: true,
    });
  });
  const assignedTo = urlAssignedTo ?? storedAssignedTo;
  // An assignee the practice cannot express is worse than none: the pill falls back to "Anyone" for a
  // practitioner it can't find while the query stays narrowed to them, so the queue reads empty under a
  // pill that says it isn't filtered.
  useAssigneeWithinPractice(organizationId, assignedTo, () => {
    setStoredAssignedTo(undefined);
    void navigate({ search: (prev) => ({ ...prev, assignedTo: undefined, pageIndex: 0 }), replace: true });
  });
  // The URL value is schema-validated but the stored one isn't, so a tampered value is dropped here.
  // First-run default is 'due' (issue #8928) — it only reaches users with no stored pick, since the
  // pill is sticky.
  const due = (urlDue === 'overdue' ? 'due' : urlDue) ?? parseStoredWorklistDue(storedDue) ?? 'due';

  return (
    <HomePage
      showDaySchedule={scheduleEnabled}
      morningBriefingSlot={
        timelineNarration === true ? (
          <Suspense fallback={null}>
            <MorningBriefingButton />
          </Suspense>
        ) : null
      }
      organizationId={organizationId}
      // Changing the org also clears the assignee: the pill's options are scoped to the org's
      // directory, so a practitioner picked under the old org may not exist in the new one — the
      // pill would silently read "Anyone" while the list stayed narrowed by the stale id.
      setOrganizationId={(organizationId) => {
        setStoredOrganizationId(organizationId);
        setStoredAssignedTo(undefined);
        void navigate({ search: (prev) => ({ ...prev, organizationId, assignedTo: undefined, pageIndex: 0 }) });
      }}
      assignedTo={assignedTo}
      setAssignedTo={(assignedTo) => {
        setStoredAssignedTo(assignedTo);
        void navigate({ search: (prev) => ({ ...prev, assignedTo, pageIndex: 0 }) });
      }}
      due={due}
      setDue={(due) => {
        setStoredDue(due);
        void navigate({ search: (prev) => ({ ...prev, due, pageIndex: 0 }) });
      }}
      groupByPatient={groupByPatient}
      // Page reset is not optional here: the two modes page over different things — tasks in one,
      // patients in the other — so page 3 of a flat list names nothing in a grouped one.
      setGroupByPatient={(groupByPatient) => {
        void navigate({ search: (prev) => ({ ...prev, groupByPatient, pageIndex: 0 }) });
      }}
      showFlagged={showFlagged}
      // Changes membership rather than restyling rows, so the page it was on no longer describes the
      // same list.
      setShowFlagged={(showFlagged) => {
        void navigate({ search: (prev) => ({ ...prev, showFlagged, pageIndex: 0 }) });
      }}
      pageIndex={pageIndex}
      setPageIndex={(pageIndex) => {
        void navigate({ search: (prev) => ({ ...prev, pageIndex }), replace: true });
      }}
      taskId={task}
      taskPatientId={taskPatientId}
      composingTask={composeTask}
      // Replaced rather than pushed: a practitioner clicking down a queue of tasks shouldn't have to
      // walk back through every one of them to leave the worklist.
      openTask={(taskPatientId, task) => {
        void navigate({ search: (prev) => ({ ...prev, task, taskPatientId, composeTask: false }), replace: true });
      }}
      // Both cleared so the panel opens on a blank form rather than whichever task was open, and with
      // no patient carried over from it — the form's own field is what names one.
      openNewTask={() => {
        void navigate({
          search: (prev) => ({ ...prev, task: undefined, taskPatientId: undefined, composeTask: true }),
          replace: true,
        });
      }}
      closeTask={() => {
        void navigate({
          search: (prev) => ({ ...prev, task: undefined, taskPatientId: undefined, composeTask: false }),
          replace: true,
        });
      }}
    />
  );
};

/**
 * No `crumb` of its own, deliberately: the `_auth` layout above already contributes "Home", and
 * `Breadcrumbs` renders every match that has one — so adding it here reads "Home / Home".
 */
export const Route = createFileRoute('/_auth/home/')({
  component: WorklistRoute,
  validateSearch: WorklistSearchSchema,
});
